import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import NyansaPanel from './NyansaPanel';

interface NyansaButtonProps {
  currentSection?: string;
  userName?: string;
  isAuthenticated?: boolean;
}

const NyansaButton: React.FC<NyansaButtonProps> = ({ 
  currentSection = 'hub',
  userName = 'there',
  isAuthenticated = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSuggestion, setHasSuggestion] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Simulate AI having contextual suggestions based on user behavior
  useEffect(() => {
    // After 10 seconds on a page, show a suggestion pulse
    const timer = setTimeout(() => {
      setHasSuggestion(true);
      
      // Start the pulse animation
      setIsAnimating(true);
      
      // Stop the animation after 3 pulses
      setTimeout(() => {
        setIsAnimating(false);
      }, 6000); // 3 pulses at 2 seconds each
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [currentSection]);
  
  // Reset suggestion state when changing sections
  useEffect(() => {
    setHasSuggestion(false);
    setIsAnimating(false);
  }, [currentSection]);
  
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 md:w-18 md:h-18 rounded-full flex items-center justify-center transition-all duration-300 ${
          isAnimating ? 'animate-[pulse_2s_ease-in-out_infinite]' : ''
        } backdrop-blur-md bg-white/30 dark:bg-gray-900/30 border border-white/20 dark:border-gray-700/30 shadow-md shadow-black/10 hover:bg-white/50 dark:hover:bg-gray-800/50`}
        aria-label="Open Nyansa AI Assistant"
      >
        <div className="relative">
          <MessageCircle 
            size={28} 
            className={`text-green-600 dark:text-green-400 transition-all duration-300 ${
              hasSuggestion ? 'scale-110' : ''
            }`} 
          />
          
          {/* Suggestion indicator */}
          {hasSuggestion && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <NyansaPanel 
          onClose={() => setIsOpen(false)} 
          currentSection={currentSection}
          userName={userName}
          isAuthenticated={isAuthenticated}
        />
      )}
    </>
  );
};

export default NyansaButton;